/*
 * As Informações do canal (ChannelInfoModal do ramon/chat) e o formulário do
 * canal (ChannelFormModal) com o que o chat do tema tem a mais:
 *
 * - (7) o nome do chat em grupo, que qualquer participante muda (pelo
 *   endpoint do tema: o ramon/chat só deixa o moderador do chat);
 * - (11) "Adicionar canal aos favoritos";
 * - (20) o lembrete de retenção ("As mensagens deste canal são mantidas por
 *   N dias"), com as configurações do próprio ramon/chat;
 * - (21) excluir o canal pede o nome dele digitado, em vez do "OK" do
 *   `confirm()`.
 *
 * Os dois modais vêm em chunks do ramon/chat: os extends usam o nome do módulo.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import Switch from 'flarum/common/components/Switch';
import { apiUrl, attrs, childList, component, ct, feature, findByClass, rt } from './flags';
import { isStarred, toggleStar } from './starred';
function section(label, body, className = '') {
  return m("div", {
    className: `ChatChannelInfo-section ${className}`.trim()
  }, m("div", {
    className: "ChatChannelInfo-section-label"
  }, label), body);
}

/** O nome do grupo, com Salvar. Só numa MD em que o leitor participa. */
function nameSection(modal, channel) {
  if (!feature('group_name') || !channel.isDirect?.() || !channel.isFollowing?.() || channel.isArchived?.()) return null;
  modal.__canelaName ??= channel.name?.() || '';
  const save = async () => {
    modal.__canelaSavingName = true;
    try {
      const payload = await app.request({
        method: 'POST',
        url: apiUrl(`/chat-channels/${channel.id()}/canela-name`),
        body: {
          data: {
            attributes: {
              name: modal.__canelaName
            }
          }
        }
      });
      if (payload?.meta?.serialized) app.store.pushPayload(payload);
      app.alerts.show({
        type: 'success'
      }, ct('settings.saved'));
    } catch {
      // O alerta do core diz o motivo.
    } finally {
      modal.__canelaSavingName = false;
      m.redraw();
    }
  };
  return section(ct('about_view.name'), m("form", {
    className: "CanelaRamonChat-groupName",
    onsubmit: e => {
      e.preventDefault();
      save();
    }
  }, m("input", {
    className: "FormControl",
    maxlength: 50,
    placeholder: ct('new_message_modal.create_group_placeholder'),
    value: modal.__canelaName,
    oninput: e => modal.__canelaName = e.target.value
  }), m(Button, {
    type: "submit",
    className: "Button",
    loading: !!modal.__canelaSavingName,
    disabled: (modal.__canelaName || '') === (channel.name?.() || '')
  }, ct('save')), m("p", {
    className: "helpText"
  }, rt('group_name.help'))), 'CanelaRamonChat-nameSection');
}
function starSection(channel) {
  if (!feature('starred') || !app.session.user) return null;
  return m("div", {
    className: "ChatChannelInfo-section CanelaRamonChat-starSection"
  }, m(Switch, {
    state: isStarred(channel.id()),
    onchange: () => toggleStar(channel.id())
  }, ct('channel_settings.star_channel')));
}
function retentionSection(channel) {
  const data = attrs();
  if (!feature('retention') || !data) return null;
  const days = channel.isDirect?.() ? data.dmRetentionDays : data.retentionDays;
  return section(ct('settings.history_label'), m("p", {
    className: "CanelaRamonChat-retention chat-retention-reminder-text"
  }, days ? ct('retention_reminders.long', {
    count: days
  }) : ct('retention_reminders.indefinitely_long_plain')), 'CanelaRamonChat-retentionSection');
}

// ─── (21) Excluir com o nome digitado ───────────────────────────────────────

/** O rótulo que o leitor precisa digitar: o nome do canal (ou o que a tela mostra). */
function expectedName(channel) {
  return String(channel.name?.() || channel.displayName?.() || '').trim();
}

/** Roda o `destroy()` original sem o `confirm()` dele: a confirmação já foi o nome digitado. */
function destroyConfirmed(modal) {
  const native = window.confirm;
  modal.__canelaConfirmed = true;
  window.confirm = () => true;
  try {
    modal.destroy();
  } finally {
    window.confirm = native;
    modal.__canelaConfirmed = false;
    modal.__canelaDeleting = false;
  }
}
function deleteConfirmation(modal, channel) {
  const expected = expectedName(channel);
  const typed = String(modal.__canelaTyped || '').trim();
  const matches = typed !== '' && typed.toLowerCase() === expected.toLowerCase();
  return m("div", {
    className: "CanelaRamonChat-deleteConfirm"
  }, m("p", {
    className: "CanelaRamonChat-deleteConfirm-text"
  }, ct('channel_delete.instructions', {
    name: expected
  })), m("input", {
    className: "FormControl",
    placeholder: ct('channel_delete.confirm_channel_name'),
    "aria-label": ct('channel_delete.confirm_channel_name'),
    value: modal.__canelaTyped || '',
    oninput: e => modal.__canelaTyped = e.target.value,
    oncreate: vnode => vnode.dom.focus()
  }), m("div", {
    className: "CanelaRamonChat-deleteConfirm-actions"
  }, m(Button, {
    className: "Button Button--danger",
    icon: "fas fa-trash",
    disabled: !matches,
    onclick: () => destroyConfirmed(modal)
  }, ct('channel_delete.confirm')), m(Button, {
    className: "Button Button--flat",
    onclick: () => {
      modal.__canelaDeleting = false;
      modal.__canelaTyped = '';
    }
  }, ct('cancel'))));
}
function installDeleteConfirm(modalName, sectionMethod, dangerClass) {
  override(component(modalName), 'destroy', function (original) {
    if (!feature('delete_confirm') || this.__canelaConfirmed) return original();
    this.__canelaDeleting = true;
    this.__canelaTyped = '';
    m.redraw();
    return Promise.resolve();
  });
  extend(component(modalName), sectionMethod, function (vnode) {
    if (!this.__canelaDeleting || !vnode) return;
    const channel = this.attrs.channel;
    const danger = findByClass(vnode, dangerClass);
    if (!danger || !channel) return;
    danger.children = [deleteConfirmation(this, channel)];
    danger.text = undefined;
  });
}
export default function installChannelInfo() {
  extend(component('ChannelInfoModal'), 'settings', function (vnode) {
    const channel = this.attrs?.channel;
    if (!vnode || !channel) return;
    const children = childList(vnode);
    // Depois do lugar da descrição (o primeiro filho, mesmo vazio), antes das notificações.
    children.splice(1, 0, nameSection(this, channel), starSection(channel), retentionSection(channel));
  });
  installDeleteConfirm('ChannelInfoModal', 'moderation', 'ChatChannelInfo-danger');
  installDeleteConfirm('ChannelFormModal', 'lifecycle', 'ChannelFormModal-lifecycleDanger');
}
flarum.reg.add('ramon-canela', 'forum/thirdparty/ramonChat/features/channelInfo', installChannelInfo);